import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronDown, Search, Check } from 'lucide-react';

export interface MultiSelectOption {
  value: string;
  label: string;
  category?: string;
}

interface MultiSelectProps {
  options: MultiSelectOption[];
  value: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  maxItems?: number;
  disabled?: boolean;
  error?: string;
  className?: string;
  allowCustom?: boolean;
  suggestions?: string[];
}

export const MultiSelect: React.FC<MultiSelectProps> = ({
  options,
  value = [],
  onChange,
  placeholder = 'Select options...',
  searchPlaceholder = 'Search or type custom value...',
  maxItems,
  disabled = false,
  error,
  className = '',
  allowCustom = false,
  suggestions = [],
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Ensure value is always an array
  const safeValue = Array.isArray(value) ? value : [];

  // Filter options based on search term
  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
    option.category?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Add custom option if allowed and search term doesn't match existing options
  const customOption: MultiSelectOption | null = allowCustom && searchTerm && 
    !filteredOptions.some(opt => opt.label.toLowerCase() === searchTerm.toLowerCase()) && 
    !safeValue.includes(searchTerm)
    ? { value: searchTerm, label: `Add "${searchTerm}"`, category: 'Custom' }
    : null;

  const availableOptions = customOption 
    ? [...filteredOptions, customOption]
    : filteredOptions;

  const handleSelect = (optionValue: string) => {
    if (safeValue.includes(optionValue)) {
      onChange(safeValue.filter(v => v !== optionValue));
    } else if (!maxItems || safeValue.length < maxItems) {
      onChange([...safeValue, optionValue]);
    }
    setSearchTerm('');
    setFocusedIndex(-1);
  };

  const handleRemove = (optionValue: string) => {
    onChange(safeValue.filter(v => v !== optionValue));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ') {
        setIsOpen(true);
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex(prev => 
          prev < availableOptions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex(prev => 
          prev > 0 ? prev - 1 : availableOptions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (focusedIndex >= 0 && availableOptions[focusedIndex]) {
          handleSelect(availableOptions[focusedIndex].value);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSearchTerm('');
        setFocusedIndex(-1);
        break;
      case 'Backspace':
        if (!searchTerm && safeValue.length > 0) {
          onChange(safeValue.slice(0, -1));
        }
        break;
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
        setFocusedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedLabels = safeValue.map(v => {
    const option = options.find(opt => opt.value === v);
    return option ? option.label : v;
  });

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Main input area */}
      <div
        className={`
          relative min-h-[48px] w-full px-4 py-2 bg-neutral-50 border rounded-xl
          transition-all duration-200 ease-out cursor-text
          ${isOpen ? 'border-primary-500 bg-white shadow-glow-primary' : 'border-neutral-300'}
          ${error ? 'border-error-500 bg-error-50' : ''}
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-neutral-100 hover:border-neutral-400'}
        `}
        onClick={() => !disabled && (setIsOpen(true), inputRef.current?.focus())}
      >
        {/* Selected items */}
        <div className="flex flex-wrap gap-2 mb-2">
          <AnimatePresence>
            {safeValue.map((item, index) => (
              <motion.div
                key={item}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.2 }}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-100 text-primary-700 
                          rounded-lg text-sm font-medium border border-primary-200"
              >
                <span>{selectedLabels[index]}</span>
                {!disabled && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemove(item);
                    }}
                    className="ml-1 hover:bg-primary-200 rounded-full p-0.5 transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {/* Input field */}
        <div className="flex items-center gap-2">
          <div className="flex-1 flex items-center gap-2">
            {isOpen && <Search className="w-4 h-4 text-neutral-400" />}
            <input
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={
                safeValue.length === 0 ? placeholder : isOpen ? searchPlaceholder : ''
              }
              className="flex-1 bg-transparent outline-none text-neutral-900 placeholder:text-neutral-400"
              disabled={disabled}
            />
          </div>
          
          <motion.div
            animate={{ rotate: isOpen ? 180 : 0 }}
            transition={{ duration: 0.2 }}
            className="text-neutral-400"
          >
            <ChevronDown className="w-4 h-4" />
          </motion.div>
        </div>

        {/* Max items indicator */}
        {maxItems && safeValue.length >= maxItems && (
          <div className="absolute top-2 right-12 text-xs text-neutral-500 bg-neutral-100 px-2 py-1 rounded-md">
            Max {maxItems} items
          </div>
        )}
      </div>

      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="absolute z-50 w-full mt-2 bg-white border border-neutral-200 rounded-xl shadow-strong max-h-64 overflow-hidden"
          >
            <div className="overflow-y-auto max-h-64 py-2">
              {/* Suggestions */}
              {suggestions.length > 0 && searchTerm === '' && (
                <>
                  <div className="px-4 py-2 text-xs font-semibold text-neutral-500 uppercase tracking-wider">
                    Suggestions
                  </div>
                  {suggestions.map((suggestion, index) => (
                    <button
                      type="button"
                      key={`suggestion-${suggestion}`}
                      onClick={() => handleSelect(suggestion)}
                      className={`
                        w-full text-left px-4 py-2.5 hover:bg-neutral-50 transition-colors
                        flex items-center justify-between
                        ${safeValue.includes(suggestion) ? 'bg-primary-50 text-primary-700' : 'text-neutral-900'}
                      `}
                    >
                      <span>{suggestion}</span>
                      {safeValue.includes(suggestion) && <Check className="w-4 h-4 text-primary-500" />}
                    </button>
                  ))}
                  <div className="border-t border-neutral-100 my-2" />
                </>
              )}

              {/* Options */}
              {availableOptions.length > 0 ? (
                availableOptions.map((option, index) => {
                  const isSelected = safeValue.includes(option.value);
                  const isFocused = index === focusedIndex;
                  const isCustom = option.value === searchTerm;

                  return (
                    <button
                      type="button"
                      key={option.value}
                      onClick={() => handleSelect(option.value)}
                      className={`
                        w-full text-left px-4 py-2.5 transition-colors flex items-center justify-between
                        ${isFocused ? 'bg-primary-50' : 'hover:bg-neutral-50'}
                        ${isSelected ? 'bg-primary-50 text-primary-700' : 'text-neutral-900'}
                        ${isCustom ? 'border-l-4 border-secondary-400' : ''}
                      `}
                    >
                      <div>
                        <span className={isCustom ? 'font-medium' : ''}>{option.label}</span>
                        {option.category && (
                          <span className="ml-2 text-xs text-neutral-500">
                            in {option.category}
                          </span>
                        )}
                      </div>
                      {isSelected && <Check className="w-4 h-4 text-primary-500" />}
                    </button>
                  );
                })
              ) : (
                <div className="px-4 py-6 text-center text-neutral-500">
                  {searchTerm ? 'No options found' : 'No options available'}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Error message */}
      {error && (
        <motion.p
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-2 text-sm text-error-600"
        >
          {error}
        </motion.p>
      )}
    </div>
  );
}; 